import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';

interface NavigationItem {
  name: string;
  href: string;
  current: boolean;
  icon: React.ComponentType<{ className?: string }>;
}

interface HeaderNavigationProps {
  isDarkMode: boolean;
  navigation: NavigationItem[];
}

const HeaderNavigation: React.FC<HeaderNavigationProps> = ({ isDarkMode, navigation }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close mobile menu when resizing to desktop
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      const mobileMenu = document.getElementById('mobile-navigation');
      const mobileToggle = target.closest('[aria-controls="mobile-navigation"]');

      if (isMobileMenuOpen && mobileMenu && !mobileMenu.contains(target) && !mobileToggle) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="desktop-only flex items-center gap-4 lg:gap-6" role="navigation" aria-label="Main navigation">
        {navigation.map((item) => {
          const IconComponent = item.icon;
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`
                flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                ${item.current
                  ? isDarkMode
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-100 text-gray-900'
                  : isDarkMode
                    ? 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }
              `}
              aria-current={item.current ? 'page' : undefined}
            >
              <IconComponent className="h-4 w-4" aria-hidden="true" />
              {item.name}
            </Link>
          );
        })}
      </nav>

      {/* Mobile Menu Toggle */}
      <div className="mobile-only">
        <button
          onClick={toggleMobileMenu}
          className={`
            touch-target-comfortable theme-focus-ring
            p-2 rounded-md transition-colors duration-200
            ${isDarkMode
              ? 'text-gray-300 hover:text-white hover:bg-gray-800'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }
          `}
          aria-label={isMobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu'}
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-navigation"
        >
          {isMobileMenuOpen ? (
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          ) : (
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          )}
        </button>

        {/* Mobile Menu Overlay */}
        <div
          className={`
            mobile-nav-overlay ${isMobileMenuOpen ? 'active' : ''}
            ${isDarkMode ? 'bg-black/60' : 'bg-gray-600/75'}
          `}
          onClick={toggleMobileMenu}
          aria-hidden="true"
        />

        {/* Mobile Navigation Menu */}
        <nav
          id="mobile-navigation"
          className={`
            mobile-nav-sidebar ${isMobileMenuOpen ? 'active' : ''}
            transition-all duration-300 cubic-bezier(0.16, 1, 0.3, 1) 
            shadow-lg border-r safe-area-inset-left
            ${isDarkMode
              ? 'bg-gray-900 border-gray-700 shadow-black/20'
              : 'bg-white border-gray-200 shadow-gray-900/10'
            }
          `}
          role="navigation"
          aria-label="Mobile navigation"
        >
          <div className="content-spacing-compact">
            <ul className="space-y-2">
              {navigation.map((item) => {
                const IconComponent = item.icon;
                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`
                        nav-item touch-target-comfortable theme-focus-ring
                        flex items-center px-4 py-3 rounded-lg 
                        text-sm font-semibold transition-all duration-200
                        ${item.current
                          ? isDarkMode
                            ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg shadow-primary-900/25'
                            : 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/25'
                          : isDarkMode
                            ? 'text-gray-300 hover:bg-gray-800 hover:text-white'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }
                      `}
                      aria-label={`Navigate to ${item.name}`}
                      aria-current={item.current ? 'page' : undefined}
                    >
                      <IconComponent
                        className="h-5 w-5 mr-3 transition-all duration-200 flex-shrink-0"
                        aria-hidden="true"
                      />
                      <span className="transition-all duration-200 font-medium tracking-tight">
                        {item.name}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </nav>
      </div>
    </>
  );
};

export default HeaderNavigation;